import * as React from "react";
import { useNavigate } from "@opendash/router";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { AppLayout } from "./AppLayout";
import { useTexts } from "../hooks/useTexts";
import {
  TabContainer,
  TabButton,
  ContentContainer,
  Card,
  InfoBox,
  ImageContainer,
  FigureCaption,
  SourcesSection,
} from "./common/StyledComponents";
import { LinkifyText } from "./common/LinkifyText";
import { processInlineFormatting } from "./common/RenderText";
import styled from "styled-components";

// Asset Imports
const wasserknapp1 = require("../../assets/wasserknapp1.png");
const wasserknapp2 = require("../../assets/wasserknapp2.png");
const wasserknapp3 = require("../../assets/wasserknapp3.png");
const wasserknapp4 = require("../../assets/wasserknapp4.png");

// Custom Styled Components (spezifisch für diese Route)
const HighlightBox = styled.div`
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border-left: 4px solid #4caf50;
  padding: 20px;
  margin: 20px 0;
  border-radius: 0 8px 8px 0;

  &::before {
    content: "FAZIT";
    display: block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #2e7d32;
    opacity: 0.6;
    margin-bottom: 8px;
  }

  h4 {
    color: #2e7d32;
    margin-bottom: 10px;
  }

  p {
    margin-bottom: 8px;
  }
`;

const FormulaBox = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  padding: 15px;
  margin: 20px 0;
  border-radius: 8px;
  text-align: center;
  font-family: "Courier New", monospace;
  font-size: 16px;
  font-weight: bold;
  color: #495057;
`;

const ChartContainer = styled.div`
  margin: 30px 0;

  p {
    font-size: 0.9em;
    color: #666;
    text-align: center;
    margin-top: 10px;
    font-style: italic;
  }
`;

/**
 * WasserknappheitGrundlagenRoute – Grundlagen der Wasserknappheit
 */
export const WasserknappheitGrundlagenRoute = React.memo(
  function WasserknappheitGrundlagenRoute() {
    const navigate = useNavigate();
    const { t } = useTexts();

    // Chart-Konfiguration für Süßwasser-Verteilung
    const freshwaterChartOptions: Highcharts.Options = {
      chart: {
        type: "bar",
        backgroundColor: "transparent",
        height: 400,
      },
      title: {
        text: "Gesamtvolumen an flüssigem Süßwasser (x 1 000 km³)",
        style: {
          fontSize: "14px",
          fontWeight: "normal",
        },
      },
      xAxis: {
        categories: [
          "Nordamerika",
          "Südamerika",
          "Europa",
          "Afrika",
          "Asien",
          "Australien & Ozeanien",
        ],
        title: {
          text: null,
        },
      },
      yAxis: {
        min: 0,
        max: 4000,
        title: {
          text: null,
        },
        labels: {
          format: "{value}",
        },
        gridLineWidth: 1,
        tickInterval: 500,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        bar: {
          dataLabels: {
            enabled: true,
            format: "{point.y}",
          },
          color: "#2196f3",
        },
      },
      series: [
        {
          type: "bar",
          name: "Süßwasser (x 1000 km³)",
          data: [2071, 1299, 541, 2722, 3691, 324],
        },
      ],
      credits: {
        enabled: false,
      },
    };

    const tabs = [
      {
        title: t("wasserknappheit.tab.grundlagen", "Grundlagen"),
        path: "/wasserklaerer/wasserknappheit/grundlagen",
        isActive: true,
      },
      {
        title: t("wasserknappheit.tab.entstehung", "Entstehung"),
        path: "/wasserklaerer/wasserknappheit/entstehung",
        isActive: false,
      },
      {
        title: t("wasserknappheit.tab.fussabdruck", "Wasserfußabdruck"),
        path: "/wasserklaerer/wasserknappheit/fussabdruck",
        isActive: false,
      },
    ];

    return (
      <AppLayout>
        <ContentContainer>
          <h1>{t("wasserknappheit.title", "Wasserknappheit")}</h1>

          <TabContainer>
            {tabs.map((tab, index) => (
              <TabButton
                key={index}
                isActive={tab.isActive}
                onClick={() => !tab.isActive && navigate(tab.path)}
              >
                {tab.title}
              </TabButton>
            ))}
          </TabContainer>

          <Card>
            <h2>
              {t(
                "wasserknappheit.grundlagen.section1.title",
                "Warum Wasser unser wichtigstes Gut ist"
              )}
            </h2>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.grundlagen.section1.text1",
                  "Wasser ist der Ursprung und die Grundlage allen Lebens auf der Erde. Ohne Wasser gäbe es keine Pflanzen, keine Tiere und keine Menschen – es ist die Ressource, die unsere Umwelt nährt und unser Überleben sichert; es ist in jedem Aspekt unseres Lebens präsent. [1]"
                )
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.grundlagen.section1.text2",
                  "Was geschieht, wenn diese lebenswichtige Ressource für uns Menschen nicht mehr verfügbar ist? Während ein Mensch unter normalen Bedingungen etwa zwei Monate ohne Nahrung überleben kann [2], sind es bei Wasser lediglich drei bis vier Tage – das ist zumindest die medizinische Faustregel für einen jungen gesunden Menschen. Eine genaue Vorhersage ist jedoch nicht möglich, da viele Faktoren wie die Umgebungstemperatur, die Luftfeuchtigkeit, die körperliche Aktivität, das Alter und der Gesundheitszustand am Ende tatsächlich bestimmen, wie lange ein Mensch ohne Flüssigkeitszufuhr überleben kann. [3]"
                )
              )}
            </p>

            <ImageContainer>
              <img src={wasserknapp1} alt="KI-generierte Darstellung" />
              <FigureCaption>
                Abbildung 1:{" "}
                {t(
                  "wasserknappheit.grundlagen.image1.caption",
                  "Wüstenlandschaft [generiert mit midjourney]"
                )}
              </FigureCaption>
            </ImageContainer>
          </Card>

          <Card>
            <h2>
              {t(
                "wasserknappheit.grundlagen.section2.title",
                "Was verstehen wir unter 'Wasserknappheit'?"
              )}
            </h2>
            <p>
              {t(
                "wasserknappheit.grundlagen.section2.text1",
                "Im Kapitel 'Wasserkreislauf' wird erläutert, dass Wasser nicht 'verschwinden' kann – es zirkuliert ständig im globalen Kreislauf. Wenn wir von Wasserknappheit sprechen, dann ist nur das für uns nutzbare Wasser relevant, also das Süßwasser. Folgende Grafik zeigt, wie sich die gesamten Süßwassermengen der Erde auf die Kontinente aufteilen:"
              )}
            </p>

            <ChartContainer>
              <HighchartsReact
                highcharts={Highcharts}
                options={freshwaterChartOptions}
              />
              <FigureCaption>
                Abbildung 2:{" "}
                {t(
                  "wasserknappheit.grundlagen.image2.caption",
                  "Süßwassermengenverteilung auf die Kontinente [eigene Darstellung nach UNESCO, 2022, S.13 [4]]"
                )}
              </FigureCaption>
            </ChartContainer>

            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.grundlagen.section2.text2",
                  "Betrachtet man diese Süßwasservorräte der Kontinente über die Lebensspanne eines Menschen, verändern diese sich nicht signifikant. [4] Wir laufen also nicht Gefahr, dass irgendwann alles Süßwasser verschwinden wird und nur noch Salzwasser bleibt."
                )
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.grundlagen.section2.text3",
                  "Das eigentliche Problem liegt in der räumlichen und zeitlichen Verteilung. Wasserknappheit entsteht dort, wo zu einem bestimmten Zeitpunkt nicht genug sauberes Süßwasser verfügbar ist, um den menschlichen, ökologischen und wirtschaftlichen Bedarf zu decken. Gründe dafür sind saisonale Schwankungen, der Klimawandel oder die Übernutzung durch den Menschen. Das bedeutet: Einer Region wird mehr Wasser entzogen, als durch Niederschläge oder Zuflüsse nachkommt – das Wasser geht dabei nicht verloren, sondern befindet sich lediglich an einem anderen Ort – möglicherweise dort, wo es nicht benötigt wird. [4]"
                )
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.grundlagen.section2.text4",
                  "In diesem Kapitel bezieht sich die Analyse der Wasserknappheit auf das Grundwasservorkommen. Der Fokus auf Grundwasser ist naheliegend, da es die für den Menschen wichtigste, sauberste und zugleich am leichtesten nutzbare Form von Süßwasser darstellt. Global betrachtet macht Grundwasser etwa 99 % des flüssigen Süßwassers der Erde aus und in Deutschland stammen rund 70 % des Trinkwassers aus Grundwasser. [4]"
                )
              )}
            </p>
            <p>
              {t(
                "wasserknappheit.grundlagen.section2.text5",
                "Seine besondere Bedeutung verdankt das Grundwasser nicht nur seiner Menge, sondern auch seiner Qualität. Durch das langsame Versickern des Wassers in Bodenschichten wird es auf natürliche Weise gefiltert und gereinigt. Es zählt damit zu den reinsten verfügbaren Wasserressourcen."
              )}
            </p>
            <p>
              {t(
                "wasserknappheit.grundlagen.section2.text6",
                "Vor diesem Hintergrund konzentriert sich das folgende Kapitel auf die Frage der Grundwasserknappheit und deren Implikationen für Mensch und Umwelt."
              )}
            </p>
          </Card>

          <Card>
            <h2>
              {t(
                "wasserknappheit.grundlagen.section3.title",
                "Wie messen wir Wasserknappheit?"
              )}
            </h2>
            <p>
              {t(
                "wasserknappheit.grundlagen.section3.text1",
                "Eine einfache Möglichkeit, um Wasserknappheit zu bemessen ist es, den Quotienten aus Wasserentnahme und Wasserneubildung einer Region zu betrachten."
              )}
            </p>

            <ImageContainer>
              <img src={wasserknapp3} alt="Illustration Wasserbilanz" />
              <FigureCaption>
                Abbildung 3:{" "}
                {t(
                  "wasserknappheit.grundlagen.image3.caption",
                  "Illustration Wasserbilanz [eigene Darstellung]"
                )}
              </FigureCaption>
            </ImageContainer>

            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.grundlagen.section3.text2",
                  'Dieses Prinzip kann auf verschiedene Arten angewendet werden. Deutschland verwendet beispielsweise den sogenannten "mengenmäßigen Zustand von Grundwasserkörpern". Eine übermäßige Nutzung liegt hier vor, wenn Grundwasserkörpern mehr Wasser entnommen wird als sich natürlich neubildet. [5] Doch auch Flüsse und Seen sind entscheidend: Sie dienen zur Bewässerung, stabilisieren Ökosysteme und tragen zur Grundwasserneubildung bei. [4] Es gibt daher auch den Wassernutzungsindex, der die gesamte Wassernutzung unabhängig von der Bezugsquelle anteilig zum gesamten Wasserdargebot wiedergibt. Hier liegt eine Übernutzung vor, wenn mehr als 20 % des gesamten Wasserdargebots entnommen wird. [6]'
                )
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.grundlagen.section3.text3",
                  'Problematisch ist, dass solche Indikatoren oft großräumig und über lange Zeiträume berechnet werden. Dann können sie lokale Dürren überdecken – etwa, wenn Starkregen und Überschwemmungen rechnerisch den Jahresdurchschnitt "schönrechnen". Je räumlich und zeitlich feiner solche Indikatoren gefasst sind, desto aussagekräftiger werden sie. Außerdem berücksichtigen einfache Ansätze nicht, dass ein Teil des entnommenen Wassers nach der Nutzung wieder in den Kreislauf zurückfließt. [4]'
                )
              )}
            </p>
          </Card>

          <Card>
            <h2>
              {t(
                "wasserknappheit.grundlagen.section4.title",
                'Der "Blue Water Scarcity Indicator"'
              )}
            </h2>
            <p>
              {t(
                "wasserknappheit.grundlagen.section4.text1",
                'Um diese Schwächen zu beheben, entwickelten Mekonnen und Hoekstra (2016) den sogenannten "Blue Water Scarcity Indicator" (BWSI), den auch die UN heranzieht. Auf Deutsch könnte dieser am ehesten als "Indikator für die blaue Wasserknappheit" übersetzt werden. Dabei bezieht sich der Begriff "blaues Wasser" auf oberflächennahe Gewässer und Grundwasser, die für menschliche Nutzung entnommen werden. Im Gegensatz zu anderen Indikatoren wird der BWSI monatlich berechnet.'
              )}
            </p>

            <InfoBox>
              <h4>
                {t(
                  "wasserknappheit.grundlagen.section4.definition1.title",
                  "Blue Water Footprint (Blauer Wasser-Fußabdruck)"
                )}
              </h4>
              <p>
                {t(
                  "wasserknappheit.grundlagen.section4.definition1.text",
                  "Die Menge an Wasser, die entnommen und nicht zurückgeführt wird, weil sie z. B. verdunstet oder in Produkten gebunden bleibt."
                )}
              </p>
            </InfoBox>

            <InfoBox>
              <h4>
                {t(
                  "wasserknappheit.grundlagen.section4.definition2.title",
                  "Blue Water Availability (Blaue Wasserverfügbarkeit)"
                )}
              </h4>
              <p>
                {t(
                  "wasserknappheit.grundlagen.section4.definition2.text",
                  "Das Wasser, das in einer Region tatsächlich verfügbar ist: lokaler Abfluss + Zufluss von flussaufwärts – ökologisch notwendige Restmengen (Mindestfließmenge bspw. die im Fluss erhalten bleiben muss) – Wasserentnahmen flussaufwärts"
                )}
              </p>
            </InfoBox>

            <h3>
              {t(
                "wasserknappheit.grundlagen.section4.formula.title",
                "Berechnung des BWSI"
              )}
            </h3>
            <FormulaBox>
              {t(
                "wasserknappheit.grundlagen.section4.formula.text",
                "Blue Water Scarcity Indicator = Blue Water Footprint / Blue Water Availability"
              )}
            </FormulaBox>

            <p>
              {t(
                "wasserknappheit.grundlagen.section4.text2",
                "Ein BWSI-Wert kleiner als 1 bedeutet, dass sich die Wasserentnahmen im Rahmen der Verfügbarkeit bewegen. Ein Wert größer als 1 bedeutet, dass der Wasserverbrauch im Verhältnis zur verfügbaren Wasserressource in diesem Gebiet übermäßig hoch ist, was auf eine potenzielle Wasserknappheit hindeutet. Je höher der Wert, desto stärker steht die Ressource Wasser unter Stress, und desto größer ist das Risiko, dass diese in Zukunft in dieser Region nicht mehr für Natur und Mensch ausreichen wird. Folgende Abbildung zeigt einen groben Überblick, welche Regionen weltweit unter potenziellem Wasserstress stehen in Bezug auf den BWSI:"
              )}
            </p>

            <ImageContainer>
              <img
                src={wasserknapp4}
                alt="Monatliche Werte des weltweiten BWSI"
              />
              <FigureCaption>
                Abbildung 4:{" "}
                {t(
                  "wasserknappheit.grundlagen.image4.caption",
                  "Monatliche Werte des weltweiten BWSI im Jahresdurchschnitt (eigene Darstellung nach UNESCO, 2022 [4])"
                )}
              </FigureCaption>
            </ImageContainer>

            <p>
              {processInlineFormatting(
                t(
                  "wasserknappheit.grundlagen.section4.text3",
                  "Rund vier Milliarden Menschen leben in Regionen, die mindestens einen Monat pro Jahr unter extremer Wasserknappheit leiden. [4]"
                )
              )}
            </p>
            <p>
              {t(
                "wasserknappheit.grundlagen.section4.text4",
                "Durch seine hohe zeitliche Auflösung und Detailtiefe ist der BWSI ein nützlicher Indikator, um die Nachhaltigkeit der Wasserressourcennutzung genauer und präziser im Hinblick auf die Zukunft zu bewerten."
              )}
            </p>
          </Card>

          <HighlightBox>
            <p>
              {t(
                "wasserknappheit.grundlagen.fazit.text",
                'Wasserknappheit bedeutet nicht, dass das Wasser "weg" ist – sondern dass es zur falschen Zeit am falschen Ort ist. Mit präzisen Indikatoren wie dem Blue Water Scarcity Indicator lässt sich besser erfassen, wo die Übernutzung kritisch wird und welche Regionen besonders verwundbar sind.'
              )}
            </p>
          </HighlightBox>

          <SourcesSection>
            <h3>
              {t(
                "wasserknappheit.grundlagen.literatur.title",
                "Literaturverzeichnis"
              )}
            </h3>
            <ol>
              <li id="quelle-1">
                <LinkifyText>
                  {t(
                    "wasserknappheit.grundlagen.literatur.ref1",
                    "Heinrich-Böll-Stiftung und Bund für Umwelt und Naturschutz Deutschland, Hrsg., Wasseratlas: Daten und Fakten über die Grundlage allen Lebens, 1. Auflage. Paderborn: Bonifatius Druck, 2025."
                  )}
                </LinkifyText>
              </li>
              <li id="quelle-2">
                <LinkifyText>
                  {t(
                    "wasserknappheit.grundlagen.literatur.ref2",
                    "GEO (Hrsg.), \"Wie lange kann man unter 'normalen' Bedingungen ohne Nahrung auskommen?\", GEO. Zugegriffen: 6. Oktober 2025. [Online]. Verfügbar unter: https://www.geo.de/mitmachen/frage-des-tages/frage-des-tages-20122018-wie-lange-kann-man-unter-normalen-30203122.html"
                  )}
                </LinkifyText>
              </li>
              <li id="quelle-3">
                <LinkifyText>
                  {t(
                    "wasserknappheit.grundlagen.literatur.ref3",
                    'Bundesministerium Land- und Forstwirtschaft, Klima- und Umweltschutz, Regionen und Wasserwirtschaft (Hrsg.), "Überleben ohne Wasser", genblue. Zugegriffen: 6. Oktober 2025. [Online]. Verfügbar unter: https://www.generationblue.at/wasserwissen/trinkwasser_abwasser/berleben-ohne-wasser.html'
                  )}
                </LinkifyText>
              </li>
              <li id="quelle-4">
                <LinkifyText>
                  {t(
                    "wasserknappheit.grundlagen.literatur.ref4",
                    "United Nations, Hrsg., The United Nations World Water Development Report 2022: Groundwater: Making the invisible visible. Paris: UNESCO, 2022."
                  )}
                </LinkifyText>
              </li>
              <li id="quelle-5">
                <LinkifyText>
                  {t(
                    "wasserknappheit.grundlagen.literatur.ref5",
                    'C. Baumgarten, J. Rechenberg, S. Richter, I. Chorus, L. Vigelahn, und O. Schmoll, "Wassersparen in Privathaushalten: sinnvoll, ausgereizt, übertrieben? - Fakten, Hintergründe, Empfehlungen", Umweltbundesamt, 2014. [Online]. Verfügbar unter: https://www.umweltbundesamt.de/sites/default/files/medien/479/publikationen/hgp_wassersparen_in_privathaushalten_web.pdf'
                  )}
                </LinkifyText>
              </li>
              <li id="quelle-6">
                <LinkifyText>
                  {t(
                    "wasserknappheit.grundlagen.literatur.ref6",
                    'Umweltbundesamt (Hrsg.), "WW-R-1: Wassernutzungsindex", Umweltbundesamt, 2023. Zugegriffen: 22. Oktober 2025. [Online]. Verfügbar unter: https://www.umweltbundesamt.de/monitoring-zur-das/handlungsfelder/wasserhaushalt/ww-r-1/indikator'
                  )}
                </LinkifyText>
              </li>
            </ol>
          </SourcesSection>
        </ContentContainer>
      </AppLayout>
    );
  }
);
